/** @jsxImportSource @emotion/react */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AlternateAction } from 'components/AlternateAction';
import { Button } from 'components/Button';
import { ErrorBox } from 'components/ErrorBox';
import {hasMinLength, Input, isRequired, Submit} from 'components/Form';
import { Text } from 'components/Text';
import { TrackingContext } from 'context/Tracking';
import { post, Endpoints } from 'utils/api';
import { useStyles } from 'utils/useStyles';

import { qrCodeAndVerificationStyles } from './QrCodeAndVerification.styles';

interface VerificationFormValues {
  code: string;
}

interface MfaVerifySetupResponse {
  recoveryCodes: string[];
}

interface QrCodeAndVerificationProps {
  qrCodeData: string;
  secret: string;
  onSuccess: (recoveryCodes: string[]) => void;
  onSkip?: () => void;
}

export const QrCodeAndVerification = ({
  qrCodeData,
  secret,
  onSuccess,
  onSkip,
}: QrCodeAndVerificationProps) => {
  const styles = useStyles(qrCodeAndVerificationStyles);
  const [, { faro }] = useContext(TrackingContext);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<VerificationFormValues>({
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopySecret = useCallback(async () => {
    if (secret) {
      try {
        await navigator.clipboard.writeText(secret);
        setCopySuccess(true);

        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }

        copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  }, [secret]);

  const handleVerification: SubmitHandler<VerificationFormValues> = useCallback(
    async (values) => {
      setVerificationError(null);

      try {
        const response = await post<MfaVerifySetupResponse>(Endpoints.MFA_VERIFY_SETUP, {
          token: values.code,
        });

        if (!response || !Array.isArray(response.recoveryCodes) || response.recoveryCodes.length === 0) {
          throw new Error('Invalid response from server.');
        }

        faro.trackFormSubmitSuccess('mfa-setup-verify');
        onSuccess(response.recoveryCodes);
      } catch (err: any) {
        const errorMessage = err.message || 'Invalid verification code.';
        setVerificationError(errorMessage);
        setValue('code', '', { shouldValidate: true });
        faro.trackFormSubmitError('mfa-setup-verify', errorMessage);
      }
    },
    [faro, setValue, onSuccess]
  );

  const handleSkipSetup = useCallback(() => {
    faro.trackEvent('mfa_setup_skipped');
    onSkip?.();
  }, [faro, onSkip]);

  return (
    <div css={styles.stepContent}>
      <Text align="center" type="large" css={styles.title}>
        Set up two-factor authentication
      </Text>
      <Text align="center" css={styles.instructions} type="normal">
        Scan with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
      </Text>

      <div css={styles.qrCodeContainer}>
        <img
          src={qrCodeData}
          alt="QR code for MFA setup"
          css={styles.qrCodeImage}
        />
        <>
          <Text align="center" css={styles.manualEntryText} type="normal" color="gray7">
            Or enter this secret key manually:
          </Text>
          <div css={styles.secretContainer}>
            <Text css={styles.secretText} type="normal" aria-label="Manual entry secret">
              {secret}
            </Text>
            <span title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                label={copySuccess ? 'Copied!' : 'Copy to clipboard'}
                hideLabel={true}
                icon={copySuccess ? 'clipboard-check' : 'clipboard'}
                onClick={handleCopySecret}
                className={copySuccess ? 'copied' : ''}
                css={styles.copyButton}
                variant="white"
                fill="text"
                width="auto"
                trackingLabel="mfa-setup-copy-secret"
              />
            </span>
          </div>
        </>
      </div>

      <div css={styles.divider} />

      <form onSubmit={handleSubmit(handleVerification)} noValidate css={styles.form}>
        <div css={styles.codeInputContainer}>
          <Input
            control={control}
            name="code"
            label="Verification code"
            type="text"
            showLabel
            autoFocus
            formatter={formatCodeInput}
            placeholder="Enter 6-digit code"
            validate={[
              isRequired('Please enter a verification code'),
              hasMinLength(6, 'Code must be 6 digits'),
            ]}
          />
          {verificationError && <ErrorBox>{verificationError}</ErrorBox>}

          <Submit
              label="Verify"
              isLoading={isSubmitting}
              loadingText="Verifying..."
              disabled={isSubmitting}
              trackingLabel="mfa-setup-verify"
          />
        </div>
      </form>
      {onSkip && (
        <div css={styles.actionsContainer}>
          <AlternateAction
            label=""
            actionLabel="Continue without MFA"
            actionEvent={handleSkipSetup}
            actionType="button"
            id="skip-mfa-setup"
          />
        </div>
      )}
    </div>
  );
};

const formatCodeInput = (value: string) => {
  // Only allow digits and limit to 6 characters
  const digits = value.replace(/\D/g, '').slice(0, 6);
  return digits;
};

